import { InfoOutlined } from '@mui/icons-material';
import { Button, Link, Stack, Tooltip } from '@mui/material';
import { DataGridProProps } from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import { Moment } from 'moment';
import { ReactElement } from 'react';
import { DigestAlgorithm } from '../../../common/certificates/enums/DigestAlgorithm';
import { Validity } from '../../../common/certificates/enums/Validity';
import { ValidityLength } from '../../../common/certificates/enums/ValidityLength';
import { BlankCell } from '../../../common/components/Cells/BlankCell';
import { FilterValueInputType } from '../../../common/components/FilterPanel/enum/FilterValueInputType';
import { Operation } from '../../../common/components/FilterPanel/enum/Operation';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { TypographyOverflow } from '../../../common/components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState } from '../../../common/controllers/PaginatedTableController';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { KEY_LENGTH_OPTIONS, KEY_LENGTH_OPTION_VALIDATOR } from '../../../common/keys/constants/KeyLengths';
import { FieldConfiguration } from '../../../common/types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { DateUtils } from '../../../common/utils/DateUtils';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { SuccessNotification } from '../../../common/utils/notifications/Notification';
import { IntegrationsRepository } from '../../../integrations/services/IntegrationsRepository';
import { Venafi } from '../../../integrations/venafi/models/Venafi';
import { VenafiRepository } from '../../../integrations/venafi/services/VenafiRepository';
import { PATHS } from '../../../routing/routes';
import { ReportOutletContextType } from '../../common/contexts/ReportOutletContext';
import { Report, ReportDone, ReportFailed, ReportPending } from '../../common/models/Report';
import { ReportCertificate } from '../models/ReportCertificate';
import { ReportCertificatesRepository } from '../services/ReportCertificatesRepository';

type Props = WithRouterProps<ReportOutletContextType> & {};

type State = PaginatedTableState & {
  canUpdateVenafi: boolean;
  venafi: Venafi | null;
  venafiLastUpdatedAt: Moment | null;
};

export class Controller extends PaginatedTableController<ReportCertificate, Props, State> {
  private readonly repository = new ReportCertificatesRepository();
  private readonly integrationsRepository = new IntegrationsRepository();
  private readonly venafiRepository = new VenafiRepository();

  getBaseState(): State {
    return {
      ...super.getBaseState(),
      canUpdateVenafi: false,
      venafiLastUpdatedAt: null,
      // On reset state, preserve the current `venafi` integration status. Policies and report
      // information should be re-evaluated, but the integration itself does not change
      venafi: this.state?.venafi ?? null,
    };
  }

  componentDidMount(): void {
    super.componentDidMount();

    this.loadReportContext();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    super.componentDidUpdate(prevProps, prevState);

    this.onReportContextChanged(prevProps.context.report, this.props.context.report);
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.repository.cancelAllRequests();
    this.integrationsRepository.cancelAllRequests();
    this.venafiRepository.cancelAllRequests();
  }

  protected doInitialDataFetch() {
    // nothing, we aren't fetching anything until we receive a ReportDone from context
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<ReportCertificate>> {
    const { report } = this.props.context;

    if (!(report instanceof ReportDone) || report.getCertificatesCount() === 0) return BLANK_PAGINATION_RESULT;

    try {
      const { certificates, venafiLastUpdatedAt } = await this.repository.getList(report.getId(), cursorInfo, this.getFilters(), this.getSorters());
      this.setState({ venafiLastUpdatedAt });
      return certificates;
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }

  protected renderRightToolbarElements(): ReactElement | null {
    const { venafi, canUpdateVenafi, loadingData, hadAnyData, venafiLastUpdatedAt } = this.state;
    const report = this.props.context.report;
    const reportIsDone = report instanceof ReportDone;

    if (!venafi || !canUpdateVenafi || !reportIsDone) {
      return null;
    }
    return (
      <Stack direction="row" gap={2} alignItems="center">
        <Button onClick={() => this.updateVenafiGuids(report.getId(), !venafiLastUpdatedAt)} variant="contained" disabled={!hadAnyData || loadingData}>
          {venafiLastUpdatedAt ? t('venafiUpdateGuids') : t('venafiPopulateGuids')}
        </Button>
        {venafiLastUpdatedAt ? (
          <Tooltip title={t('venafiLastUpdated', { date: DateUtils.toDateTimeString(venafiLastUpdatedAt) })}>
            <InfoOutlined sx={{ color: 'text.secondary' }} />
          </Tooltip>
        ) : null}
      </Stack>
    );
  }

  protected getFieldConfigurations(): FieldConfiguration<ReportCertificate>[] {
    const { venafiLastUpdatedAt, venafi } = this.state;
    const output: FieldConfiguration<ReportCertificate>[] = [
      {
        fieldKey: 'id',
        label: t('id'),
        description: t('id'),
        sortable: true,
        columnProps: {
          maxWidth: 100,
        },
        render: (item, { tabIndex }) => (
          <Link
            tabIndex={tabIndex}
            href={UrlHelper.generatePath(PATHS.REPORT_CERTIFICATE, {
              reportId: item.getReportId().toString(),
              certificateId: item.getId().toString(),
            })}
            aria-label={t('a11y.report.certificates.certificateIdLabel', { certId: item.getId() }) as string}
          >
            {item.getId()}
          </Link>
        ),
      },
      {
        fieldKey: 'subject',
        label: t('subject'),
        description: t('subject'),
        sortable: false,
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getSubject()}</TypographyOverflow>,
      },
      {
        fieldKey: 'issuer',
        label: t('issuer'),
        description: t('issuer'),
        sortable: false,
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getIssuer()}</TypographyOverflow>,
      },
      {
        fieldKey: 'expiryDate',
        label: t('expiryDate'),
        description: t('expiryDate'),
        sortable: true,
        render: (item, { hasFocus }) => {
          const date = item.getExpiryDate();
          if (date) {
            return <TypographyOverflow hasFocus={hasFocus}>{DateUtils.toDateTimeString(date)}</TypographyOverflow>;
          }
          return <BlankCell />;
        },
      },
      {
        fieldKey: 'digestAlgorithm',
        label: t('digestAlgorithm'),
        description: t('digestAlgorithm'),
        sortable: true,
        render: (item, { hasFocus }) => {
          const algorithm = item.getDigestAlgorithm();
          if (algorithm) {
            return <TypographyOverflow hasFocus={hasFocus}>{algorithm}</TypographyOverflow>;
          }
          return <BlankCell />;
        },
      },
      {
        fieldKey: 'publicKey',
        label: t('publicKey'),
        description: t('publicKey'),
        sortable: false,
        render: (item, { tabIndex, hasFocus }) => {
          const publicKey = item.getPublicKey();
          return (
            <Link
              tabIndex={tabIndex}
              href={UrlHelper.generatePath(PATHS.REPORT_KEY_LIFE_CYCLE, {
                reportId: item.getReportId().toString(),
                keyId: publicKey.getId().toString(),
              })}
              aria-label={t('a11y.report.certificates.publicKeyIdLabel', { keyId: publicKey.getId() }) as string}
            >
              <TypographyOverflow hasFocus={hasFocus}>{publicKey.getShortDescription()}</TypographyOverflow>
            </Link>
          );
        },
      },
      {
        fieldKey: 'fingerprint',
        label: t('fingerprint'),
        description: t('fingerprint'),
        sortable: true,
        render: (item, { hasFocus }) => {
          const fingerprint = item.getFingerprint();
          if (fingerprint) {
            return <TypographyOverflow hasFocus={hasFocus}>{fingerprint}</TypographyOverflow>;
          }
          return <BlankCell />;
        },
      },
    ];
    if (venafi) {
      output.push({
        fieldKey: 'venafiGuid',
        label: t('venafiGuid'),
        description: t('venafiGuid'),
        render: (item, { tabIndex, hasFocus }) => {
          const guid = item.getVenafiGuid();
          if (!guid) {
            return <BlankCell blankText={venafiLastUpdatedAt === null ? t('notSet') : t('notFound')} />;
          }
          const href = venafi.certificateViewUrl(guid);
          return (
            <Link
              tabIndex={tabIndex}
              href={href}
              target="_blank"
              aria-label={t('a11y.report.certificates.venafiGuidLabel', { certId: item.getId() }) as string}
            >
              <TypographyOverflow hasFocus={hasFocus}>{guid}</TypographyOverflow>
            </Link>
          );
        },
      });
    }
    return output;
  }

  protected shouldStoreFiltersInUrl(): boolean {
    return true;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [
      {
        filterKey: 'isUsed',
        label: t('used'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: [
          { value: 'TRUE', label: t('true') },
          { value: 'FALSE', label: t('false') },
        ],
      },
      {
        filterKey: 'isSelfSigned',
        label: t('selfSigned'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: [
          { value: 'TRUE', label: t('true') },
          { value: 'FALSE', label: t('false') },
        ],
      },
      {
        filterKey: 'isCa',
        label: t('caCertificate'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: [
          { value: 'TRUE', label: t('true') },
          { value: 'FALSE', label: t('false') },
        ],
      },
      {
        filterKey: 'hasPrivateKey',
        label: t('hasPrivateKey'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: [
          { value: 'TRUE', label: t('true') },
          { value: 'FALSE', label: t('false') },
        ],
      },
      {
        filterKey: 'digestAlgorithm',
        label: t('digestAlgorithm'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: Object.values(DigestAlgorithm).map((value) => ({ value, label: value.replace('_', '') })),
      },
      {
        filterKey: 'validityLength',
        label: t('validityLength'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: Object.values(ValidityLength).map((value) => ({ value, label: t(`validityLengths.${value}`) })),
      },
      {
        filterKey: 'validity',
        label: t('validity'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: Object.values(Validity).map((value) => ({ value, label: t(`validities.${value}`) })),
      },
      // TODO: see how to "label" the `search` filter (it's a freetext filter across all fields)
      {
        filterKey: 'keyLength',
        label: t('keyLength'),
        type: FilterValueInputType.SELECT,
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.GE, label: t('filterLabels.isGreaterOrEqual') },
          { value: Operation.GT, label: t('filterLabels.isGreater') },
          { value: Operation.LE, label: t('filterLabels.isLessOrEqual') },
          { value: Operation.LT, label: t('filterLabels.isLess') },
        ],
        options: KEY_LENGTH_OPTIONS(),
        validator: KEY_LENGTH_OPTION_VALIDATOR,
      },
    ];
  }

  protected getAdditionalDataGridProps(): Partial<DataGridProProps<ReportCertificate>> {
    return {
      ...super.getAdditionalDataGridProps(),
      getRowId: (model) => {
        // The table must refresh every time the Venafi GUIDs are updated. It is possible that a
        // Venafi update does not change any of the GUIDs of the certificates in the table, so we
        // also need to check for the "last update" of the report to make sure we correctly set the
        // "no Venafi GUID" label in its column, which varies depending on whether the certificate
        // was never processed, or was processed but was not found in Venafi
        return `${model.getId()}_${model.getVenafiGuid() ?? ''}_${model.getVenafiLastUpdate() ?? ''}`;
      },
    };
  }

  private async loadIntegrations() {
    try {
      const integrations = await this.integrationsRepository.getIntegrations();
      const venafi = integrations.getVenafi();
      this.setState({ venafi });
    } catch (e: unknown) {
      this.handleError(e);
    }
  }

  private async loadPermissions(reportId: number) {
    try {
      const canUpdateVenafi = await this.permissionsService.canUpdateVenafiGuids(reportId);
      this.setState({ canUpdateVenafi });
    } catch (e: unknown) {
      this.handleError(e);
    }
  }

  private loadReportContext() {
    // When the component mounts, consider that the previous report is null
    this.onReportContextChanged(null, this.props.context.report);
  }

  private onReportContextChanged(prevReport: Report | null, currReport: Report | null) {
    // Report hasn't changed, do nothing
    if (prevReport === currReport) return;

    if (prevReport !== null && prevReport.getId() !== currReport?.getId()) {
      // Report has changed (e.g. through a version selector)
      // Reset the table state (including stored data and pagination) and reload
      this.resetState(() => this.loadReportContext());
      return;
    }

    if (!currReport || currReport instanceof ReportPending) {
      // The report is not ready yet, display the initial loading state
      this.resetState();
    } else if (currReport instanceof ReportFailed) {
      // The report is failed, display the blank loaded state
      this.setupBlankState();
    } else if (currReport instanceof ReportDone) {
      // The report is done, load the related permissions and certificates
      this.loadPermissions(currReport.getId());
      this.loadIntegrations();
      super.doInitialDataFetch();
    }
  }

  private async updateVenafiGuids(reportId: number, firstRun: boolean) {
    try {
      this.setState({ loadingData: true });
      await this.venafiRepository.updateVenafiForReport(reportId);

      // The content of the Venafi GUID column might be the same as before, show a notification
      // confirming that the action was successful
      this.showMessage(new SuccessNotification(firstRun ? t('venafiPopulateSuccess') : t('venafiUpdateSuccess')));

      // Venafi information for the report and certificates may have changed, reload the table
      this.reloadTableContent();
    } catch (e: unknown) {
      this.setState({ loadingData: false });
      this.handleError(e);
    }
  }
}

export const ReportCertificatesController = withRouter<ReportOutletContextType>(Controller);
